import { LANGUAGE_API, LANGUAGE_I18N } from '../../constants/language.constants';
import type { ApiLanguage, I18nLanguage } from '../../constants/language.constants';

/**
 * Maps frontend i18n language codes to backend API language enum values.
 *
 * @param i18nLanguage - The i18n language code (e.g., 'en', 'no', 'nb')
 * @returns The API language enum value ('EN' or 'NB')
 *
 * @example
 * mapI18nLanguageToApiLanguage('en') // returns 'EN'
 * mapI18nLanguageToApiLanguage('no') // returns 'NB'
 * mapI18nLanguageToApiLanguage('nb') // returns 'NB'
 */
export function mapI18nLanguageToApiLanguage(i18nLanguage: string): ApiLanguage {
  // Map English variants to 'EN'
  if (i18nLanguage === 'en' || i18nLanguage.startsWith('en-')) {
    return LANGUAGE_API.ENGLISH;
  }

  // Map Norwegian variants to 'NB'
  // This includes 'no', 'nb', 'nn' (Norwegian Bokmål and Nynorsk)
  if (
    i18nLanguage === 'no' ||
    i18nLanguage === 'nb' ||
    i18nLanguage === 'nn' ||
    i18nLanguage.startsWith('no-') ||
    i18nLanguage.startsWith('nb-')
  ) {
    return LANGUAGE_API.NORWEGIAN;
  }

  // Default fallback to Norwegian
  return LANGUAGE_API.NORWEGIAN;
}

/**
 * Maps backend API language enum values to frontend i18n language codes.
 *
 * @param apiLanguage - The API language enum value ('EN' or 'NB')
 * @returns The i18n language code ('en' or 'no'), or null if invalid
 *
 * @example
 * mapApiLanguageToI18n('EN') // returns 'en'
 * mapApiLanguageToI18n('NB') // returns 'no'
 * mapApiLanguageToI18n(null) // returns null
 */
export function mapApiLanguageToI18n(apiLanguage: string | null): I18nLanguage | null {
  if (apiLanguage === LANGUAGE_API.ENGLISH) {
    return LANGUAGE_I18N.ENGLISH;
  }
  if (apiLanguage === LANGUAGE_API.NORWEGIAN) {
    return LANGUAGE_I18N.NORWEGIAN;
  }
  return null;
}
